import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Checkbox,
  TextField,
  Box,
  Button,
  Stack,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import axios from "axios";
import { toast } from "react-toastify";
import ReportPDF from "../reports/testReport";
import PreviewPDF from "../../preview"; // Import preview component
import { useNavigate } from "react-router-dom"; // Correct import

export default function ReportPrintDialog({
  open,
  onClose,
  selectedSampleId,
  testId,
  allReports,
  patientData,
}: {
  open: boolean;
  onClose: () => void;
  selectedSampleId?: string;
  testId?: string[];
  allReports?: any[];
  patientData?: any;
}) {
  const [options, setOptions] = useState({
    showTestHistory: true,
    historyCount: 3,
  });

  const navigate = useNavigate();

  const [reportData, setReportData] = useState<any>(null);
  const [print, setPrint] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedTests, setSelectedTests] = useState<string[]>(testId || []);
  const [payload, setPayload] = useState<any>({
    historyCount: options.historyCount,
    sampleCollectionId: selectedSampleId || "",
    performedTestIds: [],
  });
  
  // Effect to keep the payload for the API call up-to-date
  useEffect(() => {
    setPayload({
      historyCount: options.historyCount,
      performedTestIds: testId && testId.length > 0 ? testId : selectedTests,
      sampleCollectionId: selectedSampleId || "",
    });
  }, [options.historyCount, selectedSampleId, selectedTests, testId]);

  // Effect to initialize selected tests when the dialog opens
  useEffect(() => {
    if (open && allReports && allReports.length > 0) {
      if (!testId || testId.length === 0) {
        setSelectedTests(allReports.map((test) => test._id));
      } else {
        setSelectedTests(testId);
      }
    }
  }, [open, allReports, testId]);


  // --- REFACTORED LOGIC ---
  // 1. Create a single, reusable function to fetch report data
  const generateReportData = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_LAB_URL}/patient-result/printTestResults`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 201 && response.data?.data) {
        return response.data.data; // Return the fetched data on success
      } else {
        toast.error("Failed to generate report: Invalid response from server.");
        return null;
      }
    } catch (error:any) {
      console.error("Error fetching report data:", error);
      toast.error(error?.response.data.message || "Error generating report");
      return null; // Return null on failure
    }
  };

  // 2. Create specific handlers for each button
const handlePreview = async () => {
  const fetchedData = await generateReportData();
  if (fetchedData) {
    setPreviewData(fetchedData);
    setShowPreview(true);
  }
};


  const handlePrint = async () => {
    const fetchedData = await generateReportData();
    if (fetchedData) {
      // If data is fetched successfully...
      setReportData(fetchedData); // Set data for the jsPDF component
      setPrint(true); // Trigger the jsPDF component
      onClose(); // Close the dialog
    }
  };

  const handleWhatsApp = async () => {
    const fetchedData = await generateReportData();
    if (fetchedData) {
      // Extract trNo and projectId from the fetched data
      const trNumber = fetchedData?.trNo || "";
      const projectId = fetchedData?.projectId || "";
      
      // Get patient phone number
      const phoneNumber = patientData?.phonNumber || "";
      
      if (!phoneNumber) {
        toast.error("Patient phone number is not available");
        return;
      }
      
      // Create the report URL
      const reportUrl = `https://reports.pakhims.com/?tr=${trNumber}_${projectId}`;
      
      // Format phone number for WhatsApp - normalize to 92XXXXXXXXXX format
      const formattedPhone = phoneNumber
        .replace(/[^0-9]/g, '') // Remove all non-numeric characters
        .replace(/^92/, '92') // If starts with 92, keep as is
        .replace(/^03/, '923') // If starts with 03, replace with 923
        .replace(/^3/, '923') // If starts with 3, replace with 923
        .replace(/^(?!92)(.+)/, '92$1'); // If doesn't start with 92, add 92 prefix
      
      console.log("Formatted Phone Number for WhatsApp:", formattedPhone);
      // Create WhatsApp message with intro text and report link
      const message = `Your test results are ready. Please click the link below to view or download your report:\n${reportUrl}`;
      const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
      
      // Open WhatsApp in a new window
      window.open(whatsappUrl, '_blank');
      onClose(); // Close the dialog
    }
  };
  // --- END OF REFACTORED LOGIC ---


  const handleCheckbox =
    (name: keyof typeof options) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setOptions({ ...options, [name]: event.target.checked });
    };

  const handleTestSelection =
    (testId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedSelectedTests = event.target.checked
        ? [...selectedTests, testId]
        : selectedTests.filter((id) => id !== testId);
      setSelectedTests(updatedSelectedTests);
    };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
          }}
        >
          Generate Test Result
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        {patientData && (
          <Box sx={{ px: 2, pb: 1, borderBottom: "1px solid #e0e0e0" }}>
            <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", gap: 1 }}>
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                <strong>MR No:</strong> {patientData.mrn || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                <strong>Ref MR No:</strong> {patientData.ref_mrn || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                <strong>Patient Name:</strong> {patientData.patientName || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                <strong>Guardian Name:</strong> {patientData.guardiansName || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                <strong>Gender:</strong> {patientData.gender || "N/A"}
              </Typography>
            </Stack>
          </Box>
        )}
        <DialogContent sx={{ p: 2 }}>
          {allReports && allReports.length > 0 && (
             <Table size="small" sx={{ minWidth: 650, '& .MuiTableCell-root': { padding: '2px 6px', fontSize: '0.8rem' } }}>
                <TableHead sx={{ backgroundColor: '#1976d2' }}>
                    <TableRow sx={{ height: 20 }}>
                        <TableCell sx={{ color: 'white' }}>
                            <Checkbox size="small"
                                sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                                checked={allReports.length > 0 && selectedTests.length === allReports.length}
                                onChange={(e) => setSelectedTests(e.target.checked ? allReports.map(t => t._id) : [])}
                            />
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sr. #</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Test Code</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Display Name</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Test Name</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Template</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Approval Status</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {allReports.map((test, i) => (
                        <TableRow key={test._id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' }, height: 28 }}>
                            <TableCell padding="checkbox">
                                <Checkbox size="small" checked={selectedTests.includes(test._id)} onChange={handleTestSelection(test._id)} />
                            </TableCell>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{test.testCode}</TableCell>
                            <TableCell>{test.displayName}</TableCell>
                            <TableCell>{test.testName}</TableCell>
                            <TableCell>{test.testResultType}</TableCell>
                            <TableCell>{test.approvalStatus}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={options.showTestHistory}
                  onChange={handleCheckbox("showTestHistory")}
                />
              }
              label="Show test history"
            />
            <TextField
              size="small"
              type="number"
              value={options.historyCount}
              onChange={(e) => {
                let val = Number(e.target.value);
                if (val < 0) val = 0;
                if (val > 3) val = 3;
                setOptions({ ...options, historyCount: val });
              }}
              inputProps={{ min: 0, max: 3 }}
              sx={{ width: 60 }}
            />
          </Box>

          <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2 }}>
            <Button
              onClick={handleWhatsApp}
              size="small"
              variant="contained"
              color="success"
              startIcon={<WhatsAppIcon fontSize="small" />}
            >
              WhatsApp
            </Button>
            <Button
              onClick={handlePreview}
              size="small"
              variant="contained"
              color="primary"
              startIcon={<VisibilityIcon fontSize="small" />}
            >
              Preview
            </Button>
            <Button
              onClick={handlePrint}
              size="small"
              variant="contained"
              color="info"
              startIcon={<PrintIcon fontSize="small" />}
            >
              Print
            </Button>
            <Button size="small" variant="outlined" onClick={onClose}>
              Close
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      {/* Print functionality - uses ReportPDF component */}
      {print && reportData && (
        <ReportPDF print={true} data={reportData} onClose={() => setPrint(false)} />
      )}
      {/* Preview functionality - uses PreviewPDF component */}
      {showPreview && previewData && (
        <PreviewPDF print={true} data={previewData} onClose={() => {
          setShowPreview(false);
          setPreviewData(null);
        }} />
      )}
    </>
  );
}